import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SbBlokData } from "@storyblok/react";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { getDescriptionFromBody } from "@utils/getDescriptionFromBody";
import { getSlugInLocale } from "@utils/getDataInLocale";
import { createBreadcrumb } from "@utils/slugUtils";
import { searchResultsItemsCount } from "../../constants";
import {
  StoryblokSearchResultsProps,
  StoryblokResultItemProps,
} from "../../types/SearchTypes";
import { NewsListItem } from "@components/NewsItem/NewsListItem";
import { Pagination } from "@components/Pagination";
import { useStoryblokSearch } from "@hooks/useStoryblokSearch";
import { Alert } from "@components/Alert";
import { languageEnglish } from "../../i18n";

const StoryblokResultItem = ({ story }: StoryblokResultItemProps) => {
  const { i18n, t } = useTranslation();
  const imageUrl = story.content.featuredImage?.filename;
  const source = story.content.brand?.[0]?.name || "Pon Today";
  const storyPath = getSlugInLocale(story, i18n.language);
  const description = getDescriptionFromBody(
    story.content?.body as SbBlokData[]
  );

  // Determine content type based on full_slug pattern
  const contentType = story.full_slug?.startsWith("news/")
    ? t("SearchResults.contentType.news")
    : t("SearchResults.contentType.page");

  // Create breadcrumb using utility function
  const breadcrumb = createBreadcrumb(story.full_slug);

  return (
    <NewsListItem
      title={story.name}
      slug={storyPath}
      imageUrl={imageUrl || ""}
      imageAlt={story.content?.featuredImage?.alt ?? ""}
      date={story.content.date}
      cluster={source}
      description={description || undefined}
      contentType={contentType}
      breadcrumb={breadcrumb}
    />
  );
};

const StoryblokSearchResultsSkeleton = ({
  itemsPerPage = searchResultsItemsCount,
}: {
  itemsPerPage?: number;
}) => {
  const skeletonItems = useMemo(
    () =>
      Array.from({ length: itemsPerPage }, (_, index) => ({
        id: `skeleton-${index}`,
        index,
      })),
    [itemsPerPage]
  );

  return (
    <div className="flex flex-col gap-4">
      {skeletonItems.map((item) => (
        <div
          key={item.id}
          className="flex w-full animate-pulse flex-row items-center justify-between bg-primary-50 p-4 dark:bg-white/10"
        >
          <div className="flex flex-col gap-4 pr-4">
            <div className="h-6 w-3/4 bg-gray-300 dark:bg-gray-600"></div>
            <div className="h-4 w-1/2 bg-gray-300 dark:bg-gray-600"></div>
          </div>
          <div className="h-32 w-40 shrink-0 bg-gray-300 dark:bg-gray-600"></div>
        </div>
      ))}
    </div>
  );
};

export const StoryblokSearchResults = (props: StoryblokSearchResultsProps) => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang?: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [persistedTotalCount, setPersistedTotalCount] = useState(0);
  const itemsPerPage = searchResultsItemsCount;

  // Determine which interface is being used
  const isSearchInterface =
    "searchTerm" in props && props.searchTerm !== undefined;

  const {
    data: searchData,
    isLoading: searchLoading,
    error: searchError,
  } = useStoryblokSearch(
    {
      searchTerm: isSearchInterface ? props.searchTerm : "",
      language: lang === languageEnglish ? languageEnglish : undefined,
      per_page: itemsPerPage,
      page: currentPage,
    },
    !isSearchInterface
  );

  // Determine final values based on interface
  const isLoading = isSearchInterface ? searchLoading : props.isLoading;
  const error = isSearchInterface ? searchError : null;
  const results = useMemo(
    () =>
      isSearchInterface ? searchData?.storyData || [] : props.results || [],
    [isSearchInterface, searchData?.storyData, props.results]
  );
  const searchTerm = isSearchInterface ? props.searchTerm : "";

  // Reset current page when search term changes
  useEffect(() => {
    if (isSearchInterface) {
      setCurrentPage(1);
    }
  }, [isSearchInterface, searchTerm]);

  // Update persisted total count when we have valid data
  useEffect(() => {
    const apiHeaders = isSearchInterface ? searchData?.headers : null;
    const currentTotalCount = apiHeaders?.total
      ? Number(apiHeaders.total)
      : results?.length || 0;

    // Only update if we have a valid count (> 0) or if it's the first load
    if (currentTotalCount > 0 || persistedTotalCount === 0) {
      setPersistedTotalCount(currentTotalCount);
    }
  }, [
    searchData?.headers,
    results.length,
    isSearchInterface,
    persistedTotalCount,
  ]);

  if (error && !isLoading) {
    return (
      <div className="container mx-auto max-w-4xl py-6">
        <Alert
          labelText={"Error"}
          text={t("SearchResults.loadError")}
          variant={"error"}
          textClassName="dark:text-tertiary-100"
        />
      </div>
    );
  }

  // Calculate total pages from API response (use persisted count to prevent reset during loading)
  const apiHeaders = isSearchInterface ? searchData?.headers : null;
  const currentCount = apiHeaders?.total
    ? Number(apiHeaders.total)
    : results?.length || 0;
  const totalCount = isLoading ? persistedTotalCount : currentCount;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / itemsPerPage) : 1;

  return (
    <div className="flex flex-col bg-white dark:bg-secondary-950">
      <div className="container mx-auto max-w-4xl py-6">
        <div className="mx-auto max-w-4xl px-4 pb-6">
          <div className="flex flex-col">
            <Typography className="mb-4 text-sm leading-[130%] font-normal text-tertiary-700 dark:text-white/60">
              {t("OmniSearch.resultsFound", {
                count: totalCount,
              })}
            </Typography>

            {isLoading ? (
              <StoryblokSearchResultsSkeleton itemsPerPage={itemsPerPage} />
            ) : (
              <>
                <div className="flex flex-col gap-4">
                  {results.map((story) => (
                    <StoryblokResultItem key={story.uuid} story={story} />
                  ))}
                </div>

                {isSearchInterface && totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setPage={setCurrentPage}
                    pathname={window.location.pathname}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
